import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API, { idempotentPost } from '../../utils/api';

// Order creation, cancellation, and payment endpoints are idempotent on the server.
// We send a stable Idempotency-Key per attempt so retries (network blip, double-click,
// etc.) cannot create duplicate orders or charges.

export const createOrder = createAsyncThunk(
  'orders/create',
  async (orderData, { rejectWithValue }) => {
    try {
      const { idempotencyKey, ...payload } = orderData || {};
      const { data } = await idempotentPost('/orders', payload, { idempotencyKey });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create order');
    }
  }
);

export const fetchMyOrders = createAsyncThunk('orders/fetchMine', async (params, { rejectWithValue }) => {
  try {
    const { data } = await API.get('/orders', { params });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const fetchOrder = createAsyncThunk('orders/fetchOne', async (orderId, { rejectWithValue }) => {
  try {
    const { data } = await API.get(`/orders/${orderId}`);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const cancelOrder = createAsyncThunk(
  'orders/cancel',
  async ({ orderId, reason, idempotencyKey }, { rejectWithValue }) => {
    try {
      const { data } = await API.put(
        `/orders/${orderId}/cancel`,
        { reason },
        { headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {} }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel order');
    }
  }
);

export const createRazorpayOrder = createAsyncThunk(
  'orders/razorpayOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await idempotentPost('/payments/create-order', { orderId });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Payment initiation failed');
    }
  }
);

export const verifyPayment = createAsyncThunk(
  'orders/verifyPayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      const { data } = await idempotentPost('/payments/verify', paymentData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Payment verification failed');
    }
  }
);

const orderSlice = createSlice({
  name: 'orders',
  initialState: {
    orders: [],
    order: null,
    pagination: null,
    loading: false,
    error: null,
    paymentLoading: false,
  },
  reducers: {
    clearOrderError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrder.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createOrder.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.order = payload.order;
      })
      .addCase(createOrder.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchMyOrders.pending, (state) => { state.loading = true; })
      .addCase(fetchMyOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.orders = payload?.orders || [];
        state.pagination = payload?.pagination || null;
      })
      .addCase(fetchMyOrders.rejected, (state) => { state.loading = false; })
      .addCase(fetchOrder.pending, (state) => { state.loading = true; })
      .addCase(fetchOrder.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.order = payload.order;
      })
      .addCase(fetchOrder.rejected, (state) => { state.loading = false; })
      .addCase(cancelOrder.fulfilled, (state, { payload }) => {
        state.order = payload.order;
        const idx = state.orders.findIndex(o => o._id === payload.order._id);
        if (idx !== -1) state.orders[idx] = payload.order;
      })
      .addCase(createRazorpayOrder.pending, (state) => { state.paymentLoading = true; })
      .addCase(createRazorpayOrder.fulfilled, (state) => { state.paymentLoading = false; })
      .addCase(createRazorpayOrder.rejected, (state, { payload }) => {
        state.paymentLoading = false;
        state.error = payload;
      })
      .addCase(verifyPayment.fulfilled, (state, { payload }) => {
        state.order = payload.order;
      });
  },
});

export const { clearOrderError } = orderSlice.actions;
export default orderSlice.reducer;
